import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  useTheme,
  useMediaQuery,
  DialogTitle
} from '@mui/material';
import {
  Close as CloseIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { AmslerGridData, getEyeTestedDisplay } from '../types/AmslerGridData';
import AmslerGridPlaceholder from './AmslerGridPlaceholder';
import { format } from 'date-fns';
import { useFirebaseService } from '../contexts/FirebaseServiceContext';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

interface AmslerGridDialogProps {
  open: boolean;
  onClose: () => void;
  grid: AmslerGridData | null;
  onShare: (grid: AmslerGridData) => void;
  onDownload: (grid: AmslerGridData) => void;
  onDelete?: (grid: AmslerGridData) => void;
}

const AmslerGridDialog: React.FC<AmslerGridDialogProps> = ({
  open,
  onClose,
  grid,
  onShare,
  onDownload,
  onDelete
}) => {
  const [image, setImage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [downloading, setDownloading] = useState(false);
  const { firebaseService } = useFirebaseService();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      // Clear state when dialog is closed
      setImage(null);
      setLoading(false);
      setError(false);
      return;
    }

    if (!grid) {
      console.warn('AmslerGridDialog - No grid data provided');
      setError(true);
      setLoading(false);
      return;
    }

    // Debug log for grid data 
    if (process.env.NODE_ENV === 'development') {
      console.log('AmslerGridDialog - Grid data:', {
        id: grid.id,
        timestamp: grid.timestamp,
        dotsCount: grid.dots?.length || 0,
        curvesCount: grid.curves?.length || 0,
        eyeTested: grid.eyeTested || 'unknown'
      });
    }
    
    // Generate image from grid data
    console.log('AmslerGridDialog - Generating image from grid data');
    setLoading(true);
    
    try {
      // Validate grid data before attempting to generate image
      if (!grid.dots || !Array.isArray(grid.dots)) {
        console.warn('AmslerGridDialog - Grid missing dots array');
        grid.dots = []; // Initialize empty array to avoid crashes
      }
      
      if (!grid.curves || !Array.isArray(grid.curves)) {
        console.warn('AmslerGridDialog - Grid missing curves array');
        grid.curves = []; // Initialize empty array to avoid crashes
      }
      
      const generatedImage = firebaseService.generateGridImage(grid);
      console.log('AmslerGridDialog - Image generated successfully');
      setImage(generatedImage);
      setLoading(false);
      setError(false);
      } catch (err) {
      console.error('AmslerGridDialog - Error generating image:', err);
        setError(true);
        setLoading(false);
      }
  }, [open, grid, firebaseService]);

  const formatDate = (timestamp: number | undefined) => {
    if (!timestamp || isNaN(timestamp)) return 'Unknown date';
    return format(new Date(timestamp), 'PPP');
  };

  const formatTime = (timestamp: number | undefined) => {
    if (!timestamp || isNaN(timestamp)) return '';
    return format(new Date(timestamp), 'h:mm a');
  };

  const getEyeTestedDisplay = (eyeTested: string): string => {
    switch (eyeTested?.toLowerCase()) {
      case 'left':
        return 'Left Eye';
      case 'right':
        return 'Right Eye';
      case 'both':
        return 'Both Eyes';
      default:
        return 'Not specified';
    }
  };

  const handleShare = () => {
    if (grid && onShare) {
      onShare(grid);
    }
  };

  const handleDownload = async () => {
    if (!grid || !onDownload) return;

    setDownloading(true);
    try {
      onDownload(grid);
    } catch (err) {
      console.error('Error downloading image:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteClick = () => {
    console.log('Starting delete process from dialog');
    
    // Call the parent's onDelete handler directly
    if (grid && onDelete) {
      onDelete(grid);
    }
  };

  // Keep this for handling the DeleteConfirmationDialog inside the AmslerGridDialog
  const handleConfirmDelete = () => {
    if (grid && onDelete) {
      console.log(`Confirming deletion of grid ${grid.id} from dialog's internal confirmation`);
      setDeleteConfirmOpen(false);
      onDelete(grid);
    }
  };

  if (!grid) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={true}
      PaperProps={{
        sx: { 
          maxHeight: '100%',
          height: '100%',
          overflowY: 'auto',
          m: 0,
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Android style AppBar with back button */}
      <AppBar position="sticky" elevation={0} sx={{ 
        backgroundColor: '#1A1A1A',
        zIndex: 1100,
        flexShrink: 0, // Prevent the AppBar from shrinking
        minHeight: '56px' // Reduce minimum height
      }}>
        <Toolbar 
          sx={{ 
            minHeight: '56px !important', 
            py: '4px' // Reduce padding to make toolbar more compact
          }}
        >
        <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
          aria-label="close"
            sx={{ 
              mr: 1,
              color: '#E0E0E0',
              '&:hover': {
                backgroundColor: 'rgba(187, 134, 252, 0.08)',
              }
            }}
          >
            <ArrowBackIcon />
        </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 500, color: '#E0E0E0' }}>
              Amsler Grid Test
            </Typography>
            {grid && (
            <Typography variant="subtitle2" component="div" sx={{ opacity: 0.8, fontSize: '0.875rem', color: '#A0A0A0' }}>
              {formatDate(grid.timestamp)} • {formatTime(grid.timestamp)}
              </Typography>
            )}
          </Box>
          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: '8px' }}>
            {onDelete && (
              <IconButton 
                color="error" 
                onClick={handleDeleteClick} 
                edge="end" 
                aria-label="delete"
                sx={{
                  color: '#CF6679',
                  mx: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(207, 102, 121, 0.08)',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
            <IconButton 
              color="inherit" 
              onClick={handleShare} 
              edge="end" 
              aria-label="share"
              sx={{
                color: '#BB86FC',
                mx: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(187, 134, 252, 0.08)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <ShareIcon />
            </IconButton>
            <IconButton 
              color="inherit" 
              onClick={handleDownload} 
              edge="end" 
              aria-label="download"
              disabled={downloading}
              sx={{
                color: '#03DAC6',
                mx: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(3, 218, 198, 0.08)',
                  transform: 'scale(1.1)'
                },
                '&.Mui-disabled': {
                  color: 'rgba(3, 218, 198, 0.5)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ 
        p: 0, 
        display: 'flex', 
        flexDirection: 'column', 
        height: 'calc(100% - 56px)',
        backgroundColor: '#121212',
        overflow: 'auto',
        pt: 1 // Reduced top padding to maximize height
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flex: 1, alignItems: 'center' }}>
            <CircularProgress color="primary" />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            An error occurred while loading the grid data. The data may be corrupted or incomplete.
          </Alert>
        ) : !grid ? (
          <Alert severity="warning" sx={{ m: 2 }}>
            No grid data available to display.
          </Alert>
        ) : (
          // Display grid and information - Android style layout
          <Box sx={{ 
            pb: 2, 
            display: 'flex', 
            flexDirection: 'column', 
            width: '100%',
            overflowY: 'auto'
          }}>
            {/* Grid visualization - Modern fullscreen style */}
            <Paper 
              elevation={0} 
              sx={{ 
                bgcolor: '#242424', 
                mx: { xs: 1, sm: 2 },
                mb: 1, 
                p: { xs: 1, sm: 1, md: 2 },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
                minHeight: { xs: '60vh', sm: '70vh' },
                overflow: 'visible',
                border: '1px solid #333333',
                borderRadius: 2,
                mt: { xs: 1, sm: 2 },
                borderTop: '2px solid #333333',
                pt: { xs: 1, sm: 2 },
                aspectRatio: '4/3' // Make the card more rectangular
              }}
            >
              {image ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    overflowY: 'auto',
                    overflowX: 'auto',
                    pt: 0
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: 'auto',
                      maxWidth: '100%',
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                <img
                  src={image}
                  alt="Amsler Grid Test Result"
                  onClick={onClose}
                  style={{
                        width: 'auto',
                        height: '100%',
                    maxWidth: '100%',
                    objectFit: 'contain',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                  }}
                />
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '300px',
                    height: '300px',
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                    No image available
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Modern style cards for test details */}
            <Box sx={{ px: 2 }}>
              <Paper 
                elevation={0} 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  mb: 2,
                  borderRadius: 2,
                  borderColor: '#2A2A2A',
                  backgroundColor: '#1E1E1E'
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 500,
                    color: '#E0E0E0',
                    fontSize: '1rem'
                  }}
                >
                  Test Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#A0A0A0' }}>
                      Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#E0E0E0' }}>
                      {formatDate(grid.timestamp)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#A0A0A0' }}>
                      Time
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#E0E0E0' }}>
                      {formatTime(grid.timestamp)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#A0A0A0' }}>
                      Eye Tested
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#E0E0E0' }}>
                      {getEyeTestedDisplay(grid.eyeTested || 'unknown')}
                    </Typography>
          </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#A0A0A0' }}>
                Anomalies Detected
              </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#E0E0E0' }}>
                      {grid.dots?.length || 0}
                </Typography>
                  </Box>
                </Box>
              </Paper>
              
              {grid.notes && (
                <Paper 
                  elevation={0} 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    borderColor: '#2A2A2A',
                    backgroundColor: '#1E1E1E'
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 500,
                      color: '#E0E0E0',
                      fontSize: '1rem'
                    }}
                  >
                    Notes
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      color: '#C0C0C0'
                    }}
                  >
                    {grid.notes}
                  </Typography>
                </Paper>
              )}
            </Box>
            </Box>
          )}
      </DialogContent>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName="Amsler Grid test"
      />
    </Dialog>
  );
};

export default AmslerGridDialog;